// src/App.jsx
import { useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import "./index.css";

/* ====== コードサンプル文字列 ====== */
/* --- RAG × Faiss --- */

const ragFaissCode = `
from langchain_community.document_loaders import TextLoader
from langchain.text_splitter import RecursiveCharacterTextSplitter
from langchain_openai import OpenAIEmbeddings, ChatOpenAI
from langchain_community.vectorstores import FAISS
from langchain.chains import RetrievalQA

# 1. 文書を読み込む（社内マニュアルなど）
loader = TextLoader("docs/internal_manual.txt", encoding="utf-8")
docs = loader.load()

# 2. チャンクに分割
splitter = RecursiveCharacterTextSplitter(
    chunk_size=500,
    chunk_overlap=100,
)
chunks = splitter.split_documents(docs)

# 3. 埋め込みベクトルを計算
embeddings = OpenAIEmbeddings(model="text-embedding-3-small")

# 4. Faiss ベクトルストアに登録
vectorstore = FAISS.from_documents(chunks, embeddings)

# 5. 検索＋LLM回答のRAGチェーンを構築
retriever = vectorstore.as_retriever(search_kwargs={"k": 3})
llm = ChatOpenAI(model="gpt-4.1-mini", temperature=0)

qa_chain = RetrievalQA.from_chain_type(
    llm=llm,
    retriever=retriever,
    return_source_documents=True,
)

# 6. 質問してみる
query = "mushtech のソフトハウス事業の特徴を教えてください。"
result = qa_chain.invoke({"query": query})

print("回答:")
print(result["result"])
for i, src in enumerate(result["source_documents"], 1):
    print("[{}] {}".format(i, src.metadata.get("source")))
`;

const ragFaissSaveCode = `
# Faiss の保存・読み込み（起動高速化用）
from langchain_community.vectorstores import FAISS
from langchain_openai import OpenAIEmbeddings

embeddings = OpenAIEmbeddings(model="text-embedding-3-small")

# 保存（初回）
vectorstore.save_local("faiss_index")

# 再起動時はロードするだけ
loaded_db = FAISS.load_local(
    "faiss_index",
    embeddings,
    allow_dangerous_deserialization=True,  # ローカル前提
)

docs = loaded_db.similarity_search("統計モデルのサービス内容", k=3)
for d in docs:
    print(d.page_content[:100], "...")
`;

/* --- RAG × LangChain + API --- */

const ragLangChainCode = `
from langchain_community.document_loaders import DirectoryLoader, TextLoader
from langchain.text_splitter import RecursiveCharacterTextSplitter
from langchain_openai import OpenAIEmbeddings, ChatOpenAI
from langchain_community.vectorstores import FAISS
from langchain.chains import RetrievalQA

def build_rag_chain(docs_dir: str) -> RetrievalQA:
    # 1. ディレクトリ配下の .txt をまとめて読み込み
    loader = DirectoryLoader(
        docs_dir,
        glob="**/*.txt",
        loader_cls=TextLoader,
        loader_kwargs={"encoding": "utf-8"},
    )
    docs = loader.load()

    # 2. 長文をチャンクに分割
    splitter = RecursiveCharacterTextSplitter(
        chunk_size=800,
        chunk_overlap=150,
    )
    chunks = splitter.split_documents(docs)

    # 3. 埋め込み計算
    embeddings = OpenAIEmbeddings(model="text-embedding-3-small")

    # 4. ベクトルストア作成
    vectorstore = FAISS.from_documents(chunks, embeddings)

    # 5. Retrieverに変換
    retriever = vectorstore.as_retriever(search_kwargs={"k": 4})

    # 6. LLMと組み合わせてRAGチェーンとして返す
    llm = ChatOpenAI(model="gpt-4.1-mini", temperature=0)

    chain = RetrievalQA.from_chain_type(
        llm=llm,
        retriever=retriever,
        return_source_documents=True,
    )
    return chain

if __name__ == "__main__":
    chain = build_rag_chain("docs_mushtech")
    query = "mushtech のソフトハウス事業の特徴をまとめてください。"
    result = chain.invoke({"query": query})
    print(result["result"])
`;

const ragFastApiCode = `
# FastAPI からRAGチェーンを呼び出すイメージ
from fastapi import FastAPI
from pydantic import BaseModel

app = FastAPI()
qa_chain = ...  # 起動時に build_rag_chain() などで用意しておく

class Query(BaseModel):
    question: str

@app.post("/api/rag/qa")
def rag_qa(body: Query):
    result = qa_chain.invoke({"query": body.question})
    return {
        "answer": result["result"],
        "source_count": len(result["source_documents"]),
    }
`;

/* --- ML（ロジスティック回帰・基本） --- */

const mlLogisticCode = `
import pandas as pd
from sklearn.model_selection import train_test_split
from sklearn.linear_model import LogisticRegression
from sklearn.metrics import classification_report

# CSV 読み込み（例：過去の来店データやがん診断データなど）
df = pd.read_csv("data/sample.csv")

# 目的変数と特徴量を分ける
y = df["target"]  # 例：0=来店しない / 1=来店する
X = df.drop(columns=["target"])

# 学習用とテスト用に分割
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42, stratify=y
)

# ロジスティック回帰モデル
model = LogisticRegression(max_iter=1000)

# 学習
model.fit(X_train, y_train)

# 評価
y_pred = model.predict(X_test)
print(classification_report(y_test, y_pred))
`;

/* --- Docker（超基本） --- */

const dockerComposeSample = `
version: "3.9"

services:
  app:
    build: .
    ports:
      - "8000:8000"
    volumes:
      - .:/app

  db:
    image: mariadb:11
    environment:
      - MARIADB_DATABASE=app_db
      - MARIADB_USER=app
      - MARIADB_PASSWORD=secret
      - MARIADB_ROOT_PASSWORD=rootpass
`;

/* --- SQL（超基本） --- */

const sqlSchemaSample = `
-- 顧客テーブル（最低限）
CREATE TABLE customers (
  id         BIGINT UNSIGNED AUTO_INCREMENT PRIMARY KEY,
  name       VARCHAR(255) NOT NULL,
  email      VARCHAR(255),
  created_at DATETIME,
  updated_at DATETIME
);

-- シンプルな問い合わせ例
SELECT id, name, email
FROM customers
WHERE email LIKE '%@example.com';
`;

/* ====== 共通レイアウト（ヘッダー／フッター） ====== */

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <header className="site-header">
      <div className="container header-logo-area">
        <div className="logo">
          <img src="/images/logo-hero.jpg" alt="mushtech 株式会社サンケン" className="logo-image" />
        </div>
      </div>
      <div className="header-nav-area">
        <div className="container header-nav-inner">
          <button className="hamburger" onClick={toggleMenu} aria-label="メニュー">
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
          </button>
          <nav className={`nav ${menuOpen ? 'nav-open' : ''}`}>
            <Link to="/" onClick={closeMenu}>ホーム</Link>
            <Link to="/services" onClick={closeMenu}>事業内容</Link>
            <Link to="/rag/faiss" onClick={closeMenu}>RAG入門</Link>
            <Link to="/rag/langchain" onClick={closeMenu}>RAG応用</Link>
            <Link to="/tech/ml" onClick={closeMenu}>機械学習</Link>
            <Link to="/tech/docker" onClick={closeMenu}>環境構築</Link>
            <Link to="/tech/sql" onClick={closeMenu}>データベース</Link>
            <Link to="/contact" onClick={closeMenu}>お問い合わせ</Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container">
        <small>
          &copy; {new Date().getFullYear()} 株式会社サンケン / mushtech
        </small>
      </div>
    </footer>
  );
}

/* ====== ページ1：LP（Top） ====== */

function HomePage() {
  return (
    <>
      <section className="hero" id="top">
        <div className="container hero-inner">
          <div className="hero-copy">
            <p className="hero-label">ソフトウェア開発 × AI・RAG</p>
            <h1>
              土木建築の現場知見と
              <br />
              ソフトウェア工学を掛け合わせる
            </h1>
            <p className="hero-lead">
              mushtech は、統計モデル・RAG・Webアプリケーションを核にした
              ソフトハウスプロジェクトです。
              ロジスティック回帰分析や LangChain / Faiss を使った業務チャット、
              Laravel / Flask / React による業務システムまで、
              株式会社サンケンの現場知見とあわせて設計します。
            </p>
            <div className="hero-actions">
              <a href="#services" className="btn primary">
                サービスを見る
              </a>
              <Link to="/rag/faiss" className="btn ghost">
                RAGコードサンプルを見る
              </Link>
            </div>
            <div className="hero-meta">
              <span>建設DX / 医療・解析 / CRM・業務システム</span>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-image-card">
              <img
                src="/images/hero-dev-team.jpg"
                alt="Software development team"
              />
            </div>
            <div className="hero-image-row">
              <img
                src="/images/hero-dashboard.jpg"
                alt="Analytics dashboard"
              />
              <img
                src="/images/hero-code.jpg"
                alt="Code and technology"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="about">
        <div className="container two-column">
          <div>
            <h2>現場発のソフトハウスプロジェクト「mushtech」</h2>
            <p>
              mushtech は、土木建築業を母体とする株式会社サンケンが運営する
              ソフトウェア開発プロジェクトです。
              「AIを入れたい」ではなく「事業として成立させたい」を出発点に、
              小さく作って早く試せるプロトタイピングを得意としています。
            </p>
            <p>
              統計モデルによる予測、RAGによる社内ナレッジ検索、
              そしてWebアプリとしての実装までを一気通貫で支援します。
            </p>
          </div>
          <div className="about-highlight">
            <img
              src="/images/about-office.jpg"
              alt="Office and collaboration"
              className="about-image"
            />
            <div className="about-caption">
              <h3>ソフトウェア × ドメイン知識</h3>
              <ul>
                <li>株式会社サンケンによる土木建築現場の実務経験</li>
                <li>統計解析・機械学習・RAGの実装経験</li>
                <li>Webシステム（Laravel / Flask / React / Docker）</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt" id="services">
        <div className="container">
          <h2>mushtech のサービスラインナップ</h2>
          <p className="section-lead">
            「ソフトハウスとして何ができるのか」をわかりやすく分解し、
            小さなPoCから本番運用まで段階的に支援します。
          </p>

          <div className="grid-3">
            <div className="service-card">
              <h3>1. 統計モデル・解析</h3>
              <p>
                ロジスティック回帰分析を中心とした統計モデルで、
                売上・離職・来店などを「確率」で見える化します。
              </p>
              <ul>
                <li>Python（pandas / scikit-learn）</li>
                <li>モデル評価・業務KPIへの落とし込み</li>
                <li>ダッシュボード化（Web UI）</li>
              </ul>
              <p style={{ marginTop: 8, fontSize: 12 }}>
                ▶{" "}
                <Link to="/tech/ml">ML（ロジスティック回帰）のサンプルを見る</Link>
              </p>
            </div>

            <div className="service-card">
              <h3>2. 建設DX・技術系Webアプリ</h3>
              <p>
                施工図チェック、地盤・凍上対策、工程管理など、
                建設ドメイン特化のWebアプリを開発します。
              </p>
              <ul>
                <li>Laravel / Flask バックエンド</li>
                <li>React / Vue フロントエンド</li>
                <li>Docker / Nginx / AWS構成</li>
              </ul>
              <p style={{ marginTop: 8, fontSize: 12 }}>
                ▶ <Link to="/tech/docker">Docker 構成例を見る</Link>
              </p>
            </div>

            <div className="service-card">
              <h3>3. RAG業務AI・社内チャットボット</h3>
              <p>
                LangChain + Faiss / ClickHouse で社内文書をベクトル化し、
                「聞けば答える」業務チャットを構築します。
              </p>
              <ul>
                <li>PDF / Word / Web ページの一括取り込み</li>
                <li>ベクトルDBの設計と運用</li>
                <li>既存システムとの連携</li>
              </ul>
              <p style={{ marginTop: 8, fontSize: 12 }}>
                ▶ <Link to="/rag/faiss">RAG × Faiss のサンプル</Link>{" "}
                ／ <Link to="/rag/langchain">RAG × LangChain の構成例</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt" id="contact">
        <div className="container contact-layout">
          <div className="contact-copy">
            <h2>無料PoC・無料診断（AI／データ分析）</h2>
            <p>
              Mushtechでは、RAG（社内文書AI）やデータ分析・統計モデルについて
              実データをもとにした無料PoC・無料診断を行っています。
            </p>
            <ul className="contact-points">
              <li>RAGと分析、どちらが適しているか分からない</li>
              <li>両方まとめてAI化したい</li>
              <li>まずは試してから判断したい</li>
            </ul>
            <p className="contact-note">
              このような段階でも問題ありません。<br />
              内容はメールでのやり取りのみで完結します。<br />
              <strong>※営業電話は行いません</strong>
            </p>

            <div className="pricing-compact">
              <h3>料金目安</h3>
              <ul>
                <li><strong>無料PoC・無料診断</strong>：0円</li>
                <li>データ分析／統計モデル：30万円〜</li>
                <li>RAG構築：50万円〜</li>
                <li>両対応プロジェクト：100万円〜</li>
                <li>月額保守：3万円〜</li>
              </ul>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSdjoNB8P3sjsrN0CcmBiF7xSB3pgQJZ9xOOUuK4QMvnH7rI6w/viewform?embedded=true"
              title="無料PoC・無料診断フォーム"
              className="google-form-iframe"
            >
              読み込んでいます…
            </iframe>
          </div>
        </div>
      </section>
    </>
  );
}

/* ====== 技術ページ ====== */

function RagFaissPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>RAG × Faiss サンプルコード</h1>
            <p className="section-lead">
              社内マニュアルや事業計画書などのテキストを Faiss ベクトルストアに格納し、
              LangChain を使って RAG（Retrieval-Augmented Generation）を構成する最小構成の例です。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/hero-code.jpg" alt="RAG Code" />
          </div>
        </div>

        <h2>1. 文書取り込み〜RAGチェーン構築</h2>
        <SyntaxHighlighter language="python" style={vscDarkPlus} className="code-block">
          {ragFaissCode}
        </SyntaxHighlighter>

        <h2>2. Faiss インデックスの保存と再利用</h2>
        <p className="code-note">
          インデックス作成はコストがかかるため、起動時に毎回作り直すのではなく、
          ローカルディスクやボリュームに保存して再利用します。
        </p>
        <SyntaxHighlighter language="python" style={vscDarkPlus} className="code-block">
          {ragFaissSaveCode}
        </SyntaxHighlighter>

        <div className="contact-cta-inline">
          <p>
            「自社のドキュメント構成に合わせたRAG設計をしてほしい」
            「ClickHouse や既存DBと組み合わせたい」といったご相談も承ります。
          </p>
          <a href="/#contact" className="btn primary">
            この内容で相談する
          </a>
        </div>

        <div className="pricing-section">
          <h2>RAG開発の参考価格<span className="pricing-badge">新規参入特価</span></h2>
          <p className="pricing-lead">シンプルな構成から段階的にスタートできます。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>RAG PoC構築</td>
                <td>Faiss + LangChain の最小構成、動作確認まで</td>
                <td><span className="price-value">150,000円〜</span></td>
              </tr>
              <tr>
                <td>本番向けRAG開発</td>
                <td>マルチテナント対応、API連携、管理画面付き</td>
                <td><span className="price-value">400,000円〜</span></td>
              </tr>
              <tr>
                <td>RAG運用サポート</td>
                <td>インデックス更新、チューニング、監視</td>
                <td><span className="price-value">50,000円/月〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ 規模・要件により変動します。まずはお気軽にご相談ください。</p>
        </div>
      </div>
    </main>
  );
}

function RagLangChainPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>RAG × LangChain 構成サンプル</h1>
            <p className="section-lead">
              複数ファイルをまとめて読み込み、RAGチェーンをビルドする関数と、
              それをAPIサーバから呼び出す構成のサンプルです。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/hero-dashboard.jpg" alt="RAG LangChain" />
          </div>
        </div>

        <h2>1. ディレクトリ単位でRAGチェーンを組み立てる</h2>
        <SyntaxHighlighter language="python" style={vscDarkPlus} className="code-block">
          {ragLangChainCode}
        </SyntaxHighlighter>

        <h2>2. APIサーバから呼び出すイメージ</h2>
        <p className="code-note">
          実案件では、Flask / FastAPI / Laravel など既存システムのAPI層と統合し、
          ログインユーザーやテナントごとのインデックスを切り替える設計にします。
        </p>
        <SyntaxHighlighter language="python" style={vscDarkPlus} className="code-block">
          {ragFastApiCode}
        </SyntaxHighlighter>

        <div className="contact-cta-inline">
          <p>
            「マルチテナントのRAGサービスを作りたい」「既存の基幹システムと統合したい」など、
            設計レベルからのご相談も歓迎です。
          </p>
          <a href="/#contact" className="btn primary">
            実装・設計について相談する
          </a>
        </div>

        <div className="pricing-section">
          <h2>RAG応用開発の参考価格<span className="pricing-badge">新規参入特価</span></h2>
          <p className="pricing-lead">既存システム連携やマルチテナント対応もお任せください。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>API統合開発</td>
                <td>FastAPI / Flask でのRAG API構築</td>
                <td><span className="price-value">200,000円〜</span></td>
              </tr>
              <tr>
                <td>マルチテナントRAG</td>
                <td>テナント別インデックス、権限管理付き</td>
                <td><span className="price-value">500,000円〜</span></td>
              </tr>
              <tr>
                <td>既存システム連携</td>
                <td>Laravel / 基幹システムとの統合</td>
                <td><span className="price-value">300,000円〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ 規模・要件により変動します。まずはお気軽にご相談ください。</p>
        </div>
      </div>
    </main>
  );
}

function MlPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>機械学習：ロジスティック回帰の基本</h1>
            <p className="section-lead">
              売上・来店・離職・がん診断など「はい／いいえ」を扱う二値分類でよく使う
              ロジスティック回帰の、最もシンプルな学習・評価コード例です。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/hero-dashboard.jpg" alt="機械学習" />
          </div>
        </div>

        <h2>シンプルな学習コード</h2>
        <SyntaxHighlighter language="python" style={vscDarkPlus} className="code-block">
          {mlLogisticCode}
        </SyntaxHighlighter>

        <div className="contact-cta-inline">
          <p>
            「自社データでロジスティック回帰モデルを作りたい」
            「特徴量設計から一緒にやってほしい」といったご相談も承ります。
          </p>
          <a href="/#contact" className="btn primary">
            ML活用について相談する
          </a>
        </div>

        <div className="pricing-section">
          <h2>機械学習・統計モデルの参考価格<span className="pricing-badge">新規参入特価</span></h2>
          <p className="pricing-lead">データ分析から予測モデル構築までサポートします。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>データ分析レポート</td>
                <td>既存データの可視化・傾向分析</td>
                <td><span className="price-value">80,000円〜</span></td>
              </tr>
              <tr>
                <td>予測モデル構築</td>
                <td>ロジスティック回帰・分類モデル開発</td>
                <td><span className="price-value">200,000円〜</span></td>
              </tr>
              <tr>
                <td>ダッシュボード開発</td>
                <td>予測結果の可視化Web UI</td>
                <td><span className="price-value">250,000円〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ データ量・複雑さにより変動します。まずはお気軽にご相談ください。</p>
        </div>
      </div>
    </main>
  );
}

function DockerPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>Docker を使った基本構成の例</h1>
            <p className="section-lead">
              開発環境・検証環境でよく使う、アプリケーションコンテナと
              MariaDB を組み合わせた最小構成の docker-compose.yml の例です。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/hero-dev-team.jpg" alt="Docker環境構築" />
          </div>
        </div>

        <h2>docker-compose.yml（最小サンプル）</h2>
        <SyntaxHighlighter language="yaml" style={vscDarkPlus} className="code-block">
          {dockerComposeSample}
        </SyntaxHighlighter>

        <div className="contact-cta-inline">
          <p>
            「Laravel / Flask / React をまとめてDocker化したい」
            「本番用の構成も含めて相談したい」といったご要望にも対応可能です。
          </p>
          <a href="/#contact" className="btn primary">
            Docker構成について相談する
          </a>
        </div>

        <div className="pricing-section">
          <h2>環境構築・インフラの参考価格<span className="pricing-badge">新規参入特価</span></h2>
          <p className="pricing-lead">開発環境から本番デプロイまでサポートします。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>開発環境Docker化</td>
                <td>docker-compose による開発環境構築</td>
                <td><span className="price-value">50,000円〜</span></td>
              </tr>
              <tr>
                <td>本番環境構築</td>
                <td>AWS / VPS へのデプロイ、Nginx設定</td>
                <td><span className="price-value">100,000円〜</span></td>
              </tr>
              <tr>
                <td>CI/CD構築</td>
                <td>GitHub Actions / 自動デプロイ設定</td>
                <td><span className="price-value">80,000円〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ 構成の複雑さにより変動します。まずはお気軽にご相談ください。</p>
        </div>
      </div>
    </main>
  );
}

function SqlPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>SQL / データベース設計の基本例</h1>
            <p className="section-lead">
              CRMや予約管理などでよく使う「顧客テーブル」と、
              それに対するシンプルなSELECT例です。実際の案件では、
              ここから特徴量テーブルや履歴テーブルを追加していきます。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/about-office.jpg" alt="データベース設計" />
          </div>
        </div>

        <h2>1. 顧客テーブルと基本SELECT</h2>
        <SyntaxHighlighter language="sql" style={vscDarkPlus} className="code-block">
          {sqlSchemaSample}
        </SyntaxHighlighter>

        <div className="contact-cta-inline">
          <p>
            「既存のDB構造を整理したい」「AI前提のDB設計にしたい」
            といったご相談も、スキーマ設計〜実装まで対応可能です。
          </p>
          <a href="/#contact" className="btn primary">
            SQL・DB設計について相談する
          </a>
        </div>

        <div className="pricing-section">
          <h2>データベース設計の参考価格<span className="pricing-badge">新規参入特価</span></h2>
          <p className="pricing-lead">スキーマ設計から最適化までお任せください。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>DB設計コンサル</td>
                <td>既存スキーマのレビュー・改善提案</td>
                <td><span className="price-value">50,000円〜</span></td>
              </tr>
              <tr>
                <td>新規DB設計</td>
                <td>ER図作成、テーブル設計、インデックス設計</td>
                <td><span className="price-value">120,000円〜</span></td>
              </tr>
              <tr>
                <td>パフォーマンス改善</td>
                <td>クエリ最適化、インデックスチューニング</td>
                <td><span className="price-value">80,000円〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ テーブル数・複雑さにより変動します。まずはお気軽にご相談ください。</p>
        </div>
      </div>
    </main>
  );
}

/* ====== Services ページ ====== */

function ServicesPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>mushtech のサービスラインナップ</h1>
            <p className="section-lead">
              「ソフトハウスとして何ができるのか」をわかりやすく分解し、
              小さなPoCから本番運用まで段階的に支援します。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/hero-dev-team.jpg" alt="サービス" />
          </div>
        </div>

        <div className="grid-3">
          <div className="service-card">
            <h3>1. 統計モデル・解析</h3>
            <p>
              ロジスティック回帰分析を中心とした統計モデルで、
              売上・離職・来店などを「確率」で見える化します。
            </p>
            <ul>
              <li>Python（pandas / scikit-learn）</li>
              <li>モデル評価・業務KPIへの落とし込み</li>
              <li>ダッシュボード化（Web UI）</li>
            </ul>
            <p style={{ marginTop: 8, fontSize: 12 }}>
              ▶{" "}
              <Link to="/tech/ml">ML（ロジスティック回帰）のサンプルを見る</Link>
            </p>
          </div>

          <div className="service-card">
            <h3>2. 建設DX・技術系Webアプリ</h3>
            <p>
              施工図チェック、地盤・凍上対策、工程管理など、
              建設ドメイン特化のWebアプリを開発します。
            </p>
            <ul>
              <li>Laravel / Flask バックエンド</li>
              <li>React / Vue フロントエンド</li>
              <li>Docker / Nginx / AWS構成</li>
            </ul>
            <p style={{ marginTop: 8, fontSize: 12 }}>
              ▶ <Link to="/tech/docker">Docker 構成例を見る</Link>
            </p>
          </div>

          <div className="service-card">
            <h3>3. RAG業務AI・社内チャットボット</h3>
            <p>
              LangChain + Faiss / ClickHouse で社内文書をベクトル化し、
              「聞けば答える」業務チャットを構築します。
            </p>
            <ul>
              <li>PDF / Word / Web ページの一括取り込み</li>
              <li>ベクトルDBの設計と運用</li>
              <li>既存システムとの連携</li>
            </ul>
            <p style={{ marginTop: 8, fontSize: 12 }}>
              ▶ <Link to="/rag/faiss">RAG × Faiss のサンプル</Link>{" "}
              ／ <Link to="/rag/langchain">RAG × LangChain の構成例</Link>
            </p>
          </div>
        </div>

        <div className="pricing-section">
          <h2>料金目安<span className="pricing-badge">無料診断あり</span></h2>
          <p className="pricing-lead">まずは無料PoC・無料診断からスタートできます。</p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>サービス</th>
                <th>内容</th>
                <th>参考価格（税別）</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>無料PoC・無料診断</strong></td>
                <td>実データをもとにした簡易診断</td>
                <td><span className="price-value" style={{ color: '#f97316' }}>0円</span></td>
              </tr>
              <tr>
                <td>データ分析／統計モデル</td>
                <td>可視化・予測モデル構築</td>
                <td><span className="price-value">30万円〜</span></td>
              </tr>
              <tr>
                <td>RAG構築</td>
                <td>社内文書AI・チャットボット</td>
                <td><span className="price-value">50万円〜</span></td>
              </tr>
              <tr>
                <td>両対応プロジェクト</td>
                <td>分析＋RAGの統合案件</td>
                <td><span className="price-value">100万円〜</span></td>
              </tr>
              <tr>
                <td>月額保守</td>
                <td>運用サポート・改善対応</td>
                <td><span className="price-value">3万円/月〜</span></td>
              </tr>
            </tbody>
          </table>
          <p className="pricing-footer">※ 規模・要件により変動します。まずは無料診断からお気軽にどうぞ。</p>
        </div>

        <div className="contact-cta-inline" style={{ marginTop: 40 }}>
          <p>
            RAGとデータ分析、どちらが適しているか分からなくてもOK。
            無料診断でお答えします。
          </p>
          <Link to="/contact" className="btn cta">
            無料PoC・無料診断を申し込む
          </Link>
        </div>
      </div>
    </main>
  );
}

/* ====== Contact ページ ====== */

function ContactPage() {
  return (
    <main className="section">
      <div className="container">
        <div className="page-header-with-image">
          <div className="page-header-content">
            <h1>無料PoC・無料診断（AI／データ分析）</h1>
            <p className="section-lead">
              RAG（社内文書AI）やデータ分析・統計モデルについて、
              実データをもとにした無料PoC・無料診断を行っています。
            </p>
          </div>
          <div className="page-header-image">
            <img src="/images/about-office.jpg" alt="無料診断" />
          </div>
        </div>

        <div className="contact-page-layout">
          <div className="contact-copy">
            <h2>こんな段階でもOK</h2>
            <ul className="contact-points">
              <li>RAGと分析、どちらが適しているか分からない</li>
              <li>両方まとめてAI化したい</li>
              <li>まずは試してから判断したい</li>
            </ul>
            <p className="contact-note">
              内容はメールでのやり取りのみで完結します。<br />
              <strong>※営業電話は行いません</strong>
            </p>

            <div className="pricing-compact">
              <h3>料金目安</h3>
              <ul>
                <li><strong>無料PoC・無料診断</strong>：0円</li>
                <li>データ分析／統計モデル：30万円〜</li>
                <li>RAG構築：50万円〜</li>
                <li>両対応プロジェクト：100万円〜</li>
                <li>月額保守：3万円〜</li>
              </ul>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSdjoNB8P3sjsrN0CcmBiF7xSB3pgQJZ9xOOUuK4QMvnH7rI6w/viewform?embedded=true"
              title="無料PoC・無料診断フォーム"
              className="google-form-iframe"
            >
              読み込んでいます…
            </iframe>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ====== ルート定義 ====== */

function App() {
  return (
    <div className="page">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/rag/faiss" element={<RagFaissPage />} />
        <Route path="/rag/langchain" element={<RagLangChainPage />} />
        <Route path="/tech/ml" element={<MlPage />} />
        <Route path="/tech/docker" element={<DockerPage />} />
        <Route path="/tech/sql" element={<SqlPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
